<script lang="ts" module>
	import { PLATFORM_FEE_PCT, PLATFORM_FEE_VAT_PCT } from '$lib/listing-pricing/owner-earnings';

	export type VoucherContext = 'adminFee' | 'booking' | 'capitan';

	export type AppliedVoucher = {
		code: string;
		kind: 'adminFee' | 'bookingTotal' | 'bookingCommission' | 'capitanCommission';
		discountType: 'percent' | 'fixed';
		discountValue: number;
	};

	export function formatVoucherDiscount(voucher: AppliedVoucher): string {
		return voucher.discountType === 'percent'
			? `-${voucher.discountValue}%`
			: `-${voucher.discountValue.toLocaleString('es-ES', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2
				})}€`;
	}

	/**
	 * Preview of the booking discount. Mirrors the clamping in vouchers.go
	 * (ApplyBookingVoucher); the backend value is authoritative.
	 */
	export function previewBookingDiscount(voucher: AppliedVoucher, total: number): number {
		let discount =
			voucher.discountType === 'percent'
				? (total * voucher.discountValue) / 100
				: voucher.discountValue;
		if (voucher.kind === 'bookingCommission') {
			// Cannot exceed the platform commission (owner take-home is untouched).
			discount = Math.min(discount, total * PLATFORM_FEE_PCT * (1 + PLATFORM_FEE_VAT_PCT));
		} else {
			// Stripe minimum charge ~0.50€.
			discount = Math.min(discount, Math.max(total - 0.5, 0));
		}
		return Math.max(Math.round(discount * 100) / 100, 0);
	}
</script>

<script lang="ts">
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { pb } from '@/utils/ApiClient';
	import BadgePercent from '@lucide/svelte/icons/badge-percent';
	import ShipWheel from '@lucide/svelte/icons/ship-wheel';
	import X from '@lucide/svelte/icons/x';

	let {
		context,
		applied = $bindable(null),
		disabled = false,
		tone = 'light'
	}: {
		context: VoucherContext;
		applied?: AppliedVoucher | null;
		disabled?: boolean;
		/** 'dark' adapta los colores a fondos oscuros (p. ej. la card navy). */
		tone?: 'light' | 'dark';
	} = $props();

	let expanded = $state(false);
	let code = $state('');
	let checking = $state(false);
	let errorMsg = $state('');

	async function apply() {
		const trimmed = code.trim();
		if (!trimmed || checking) return;
		checking = true;
		errorMsg = '';
		try {
			const result = await pb.send('/api/vouchers/validate', {
				method: 'POST',
				body: { code: trimmed, context }
			});
			applied = result as AppliedVoucher;
			code = '';
			expanded = false;
		} catch (e) {
			const data = (e as { response?: { error?: string } })?.response;
			errorMsg = data?.error || 'El código promocional no es válido.';
		} finally {
			checking = false;
		}
	}

	function remove() {
		applied = null;
		errorMsg = '';
	}
</script>

<div class="space-y-2">
	{#if applied}
		<div
			class="flex items-center justify-between gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2"
		>
			<div class="flex min-w-0 items-center gap-2 text-sm text-green-800">
				<BadgePercent class="size-4 shrink-0" />
				<span class="truncate">
					Código <span class="font-mono font-semibold uppercase">{applied.code}</span> aplicado
					<span class="font-semibold">({formatVoucherDiscount(applied)})</span>
				</span>
			</div>
			{#if !disabled}
				<button
					type="button"
					onclick={remove}
					class="shrink-0 rounded p-1 text-green-700 hover:bg-green-100"
					aria-label="Quitar código promocional"
				>
					<X class="size-4" />
				</button>
			{/if}
		</div>
	{:else if !expanded}
		<button
			type="button"
			onclick={() => (expanded = true)}
			disabled={disabled}
			class="text-sm font-medium underline-offset-2 hover:underline disabled:opacity-60 {tone ===
			'dark'
				? 'text-white/80 hover:text-white'
				: 'text-blue-600'}"
		>
			¿Tienes un código promocional?
		</button>
	{:else}
		<div class="space-y-1.5">
			<div class="flex gap-2">
				<Input
					bind:value={code}
					type="text"
					placeholder="Código promocional"
					class="font-mono uppercase {tone === 'dark'
						? 'h-auto rounded-[10px] border-white/16 bg-white/7 px-3.5 py-3 text-base text-white placeholder:text-zp-paper/40 focus-visible:border-white/45 focus-visible:ring-0'
						: ''}"
					disabled={checking || disabled}
					aria-invalid={errorMsg ? true : undefined}
					onkeydown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							apply();
						}
					}}
				/>
				<Button
					type="button"
					variant={tone === 'dark' ? 'default' : 'outline'}
					onclick={apply}
					disabled={checking || disabled || !code.trim()}
					class="shrink-0 {tone === 'dark'
						? 'h-auto rounded-[10px] border-transparent bg-zp-paper px-4 py-3 font-semibold text-zp-navy hover:bg-white'
						: ''}"
				>
					{#if checking}
						<ShipWheel class="size-4 animate-spin" />
					{:else}
						Aplicar
					{/if}
				</Button>
			</div>
			{#if errorMsg}
				<p class="text-sm {tone === 'dark' ? 'text-red-300' : 'text-red-600'}">{errorMsg}</p>
			{/if}
		</div>
	{/if}
</div>
